import type { FC } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import type { VenueEventListViewModel } from "src/http/types";
import { useAppContext } from "src/shared/context/AppContext";
import { useFallbackImageSrc } from "src/shared/hooks/useFallbackImageSrc";
import { VenueHeroHeader } from "src/website-apps/responsive/stubhub-venue/components/VenueHeroHeader/VenueHeroHeader";
import { venueFullGradientStyle } from "src/website-apps/responsive/stubhub-venue/components/VenueHeroHeader/VenueHeroHeader.styles";
import { VenueSubtitle } from "src/website-apps/responsive/stubhub-venue/components/VenueHeroHeader/VenueSubtitle";
import {
  StyledImageContainer,
  StyledImmersiveImage,
  StyledVenueCategoryTitleVariant,
  StyledVenueHeroContentArea,
  StyledVenueHeroInfo,
} from "src/website-apps/shared/components/ancillary-pages/BigImmersiveHeader.styled";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { ErrorMessage } from "src/website-apps/shared/components/ErrorMessage";
import { HeaderConfiguration } from "src/website-apps/shared/components/header";
import { ImageSize } from "src/website-apps/shared/components/image";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { useMegaSearchContext } from "src/website-apps/shared/components/mega-search";

// ─── Desktop/Tablet page-level hero (400px strip below nav) ──────────────────

const pageLevelHeroStyle = css({
  position: "relative",
  height: "[400px]",
  overflowX: "hidden",
  overflowY: "hidden",
  display: "flex",
  flexDirection: "column",
});

const locationTextStyle = css({
  textStyle: "label.medium",
  color: "text.onFill",
  whiteSpace: "nowrap",
  overflowX: "hidden",
  overflowY: "hidden",
  textOverflow: "ellipsis",
});

// ─── Fallback header styles (no venue image) ─────────────────────────────────

const fallbackHeaderStyle = css({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "bg",
});

const fallbackContentStyle = css({
  display: "flex",
  flexDirection: "column",
  rowGap: "primitives.100",
  width: "full",
  maxWidth: "[1270px]",
  marginInlineStart: "auto",
  marginInlineEnd: "auto",
  paddingInlineStart: "primitives.250",
  paddingInlineEnd: "primitives.250",
  paddingBlockStart: "primitives.400",
  paddingBlockEnd: "primitives.400",
});

const fallbackTitleStyle = css({
  textStyle: "display.medium",
  color: "text",
});

const fallbackLocationStyle = css({
  textStyle: "label.medium",
  color: "text.subtle",
});

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  model: VenueEventListViewModel;
  imageUrl: string | null;
  eventCount: number;
  mobileMenuOpen: boolean;
  onMobileMenuToggle: () => void;
}

export const VenueLayoutV2Header: FC<Props> = ({ model, imageUrl, eventCount, mobileMenuOpen, onMobileMenuToggle }) => {
  const appContext = useAppContext();
  const fallbackImageSrc = useFallbackImageSrc();
  const megaSearchContext = useMegaSearchContext();
  // SSR defaults to false (desktop/tablet page-level). Mobile users get immersive after hydration.
  const isPhone = !useMediaQuery(Breakpoint.Tablet);

  const subtitle = (className: string) => (
    <VenueSubtitle
      className={className}
      eventCount={eventCount}
      venueCityName={model.venueCityName}
      venueStateProvinceName={model.venueStateProvinceName}
    />
  );

  const disclosure =
    !appContext.isNativeApp && !megaSearchContext.active ? (
      <MarketplaceDisclosure marketplaceDisclosureSiteMessage={model.header.marketplaceDisclosure} />
    ) : null;

  const nav = (
    <>
      <ErrorMessage />
      {disclosure}
      <HeaderConfiguration.Browse
        isUserLoggedIn={appContext.isUserLoggedIn}
        onMobileMenuToggle={onMobileMenuToggle}
        mobileMenuOpen={mobileMenuOpen}
        bigPerformerBanner={isPhone}
        hideDiscoveryNav
      />
    </>
  );

  return (
    <MegaSearchProvider>
      {isPhone ? (
        // Mobile: immersive — nav inside hero, image behind nav (dark theme)
        imageUrl ? (
          <VenueHeroHeader
            headerTitle={model.venueName}
            imageUrl={imageUrl}
            venueCityName={model.venueCityName}
            venueStateProvinceName={model.venueStateProvinceName}
            eventCount={eventCount}
          >
            {nav}
          </VenueHeroHeader>
        ) : (
          <div className={fallbackHeaderStyle}>
            {nav}
            <div className={fallbackContentStyle}>
              <h1 className={fallbackTitleStyle}>{model.venueName}</h1>
              {subtitle(fallbackLocationStyle)}
            </div>
          </div>
        )
      ) : (
        // Desktop/Tablet: page-level — standard nav above, hero image strip below
        <>
          {nav}
          {imageUrl ? (
            <div className={pageLevelHeroStyle}>
              <StyledImageContainer>
                <StyledImmersiveImage
                  alt={model.venueName}
                  fallbackImageSrc={fallbackImageSrc}
                  size={ImageSize.GlassImage}
                  src={imageUrl}
                />
                <div className={venueFullGradientStyle} />
              </StyledImageContainer>
              <StyledVenueHeroContentArea>
                <StyledVenueHeroInfo>
                  <StyledVenueCategoryTitleVariant as="h1">{model.venueName}</StyledVenueCategoryTitleVariant>
                  {subtitle(locationTextStyle)}
                </StyledVenueHeroInfo>
              </StyledVenueHeroContentArea>
            </div>
          ) : (
            <div className={fallbackContentStyle}>
              <h1 className={fallbackTitleStyle}>{model.venueName}</h1>
              {subtitle(fallbackLocationStyle)}
            </div>
          )}
        </>
      )}
    </MegaSearchProvider>
  );
};
